import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";
import React, { useState } from "react";
import { ReportJobBatchEntry } from "../../../client/types.gen";
import { useReportJobs } from "../rater/useIntegrationJobs";
import { EqbdRateResultsDialog } from "../rater/EqbdRateResultsDialog";
import { WARNING_DISPLAY_NAMES } from "../rater/warnings";
import styles from "../rater/RaterCard.module.css";

type EqbdRaterTabProps = {
  reportId: string;
};

const STATUS_TAG_SEVERITY: Record<string, "success" | "info" | "danger"> = {
  success: "success",
  none: "info",
  failure: "danger",
};

const STATUS_LABEL: Record<string, string> = {
  success: "Success",
  none: "In Progress",
  failure: "Failed",
};

const usd = (value: number): string =>
  value.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

export const EqbdRaterTab: React.FC<EqbdRaterTabProps> = ({ reportId }) => {
  const { jobs } = useReportJobs(reportId, {
    integrationTypes: ["EQBD_RATER"],
  });
  const [resultsBatchId, setResultsBatchId] = useState<string | null>(null);

  const sortedJobs = [...jobs].sort(
    (a, b) =>
      new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
  );

  if (sortedJobs.length === 0) return null;

  return (
    <div className={styles.verticalContainerLargeGap}>
      <DataTable value={sortedJobs} size="small">
        <Column
          header="Started"
          body={(job: ReportJobBatchEntry) =>
            new Date(job.started_at + "Z").toLocaleString()
          }
        />
        <Column
          header="Status"
          body={(job: ReportJobBatchEntry) => (
            <Tag
              severity={STATUS_TAG_SEVERITY[job.outcome ?? "none"] ?? "info"}
              value={STATUS_LABEL[job.outcome ?? "none"] ?? job.outcome}
            />
          )}
        />
        <Column
          header="Locations"
          body={(job: ReportJobBatchEntry) =>
            (job.jobs[0]?.response?.location_count as number | undefined) ??
            null
          }
        />
        <Column
          header="EB Rater"
          body={(job: ReportJobBatchEntry) => {
            const total = job.jobs[0]?.response?.grand_total as
              | number
              | undefined;
            return total === undefined ? null : usd(total);
          }}
        />
        <Column
          header="John's Rater"
          body={(job: ReportJobBatchEntry) => {
            const total = job.jobs[0]?.response?.john_total as
              | number
              | undefined;
            return total === undefined ? null : usd(total);
          }}
        />
        <Column
          header="Warnings"
          body={(job: ReportJobBatchEntry) => {
            const warnings = job.jobs[0]?.response?.warnings as
              | Record<string, number>
              | undefined;
            if (!warnings || Object.keys(warnings).length === 0) return null;
            return (
              <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                {Object.entries(warnings).map(([msg, count]) => (
                  <li key={msg}>
                    {WARNING_DISPLAY_NAMES[msg] ?? msg} ({count})
                  </li>
                ))}
              </ul>
            );
          }}
        />
        <Column
          header=""
          body={(job: ReportJobBatchEntry) =>
            job.outcome === "success" ? (
              <Button
                icon="pi pi-percentage"
                text
                size="small"
                onClick={() => setResultsBatchId(job.batch_id ?? null)}
                tooltip="View premium"
                tooltipOptions={{ position: "top" }}
              />
            ) : null
          }
        />
      </DataTable>

      <EqbdRateResultsDialog
        reportId={reportId}
        batchId={resultsBatchId}
        visible={resultsBatchId !== null}
        onHide={() => setResultsBatchId(null)}
      />
    </div>
  );
};
